/**
 * Chart key levels and the ROC/ATR stats box (owner 2026-09-27, mockups 01/02 "PD-H", "ON-L",
 * "4H-L", "15M-H" lines and the ROC · TODAY · FLIP · ATR% · DR · ATR · ON% · 1h · 4h · D box).
 * Definitions: docs/chart-engine.md "Key levels and stats box". Pure functions over the chart's
 * own columnar bars, so the chart layer, the tests and any future server use share one meaning.
 *
 * - Prices stay in **integer ticks** (relative columns + `origin` → absolute ticks). Highs, lows,
 *   ranges and changes are exact integers; only averages (ATR, RSI) are fractional tick values.
 * - Trading days and 4h/1h/15m buckets are the CME session calendar (17:00 CT open, DST-aware;
 *   `calendar.ts`, ADR-0015 Q-T3), the same buckets the chart's own intraday bars use.
 * - Computed only from **intraday time bars** whose period divides the bucket (a 5m chart yields
 *   every level; a 2h chart has no 1h/15m figures; tick/range/volume/Renko and daily+ charts
 *   yield none), so no bar ever straddles a bucket.
 */
import { TradingDayCache, centralOffsetMs, centralToUtcMs, tradingDayOf, weekday } from "./calendar.ts";

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY_MS = 86_400_000;

/** Regular trading hours of the CME equity-index futures, CT minutes (08:30–15:00). */
export const RTH_OPEN_MINUTE = 8 * 60 + 30;
export const RTH_CLOSE_MINUTE = 15 * 60;
/** Wilder smoothing length for ATR and RSI. */
export const WILDER_N = 14;

/** Columnar bars (a `BarSeries` fits): prices relative to `origin`, `openTs` epoch ms. */
export type LevelBars = {
  length: number;
  origin: number | null;
  openTs: ArrayLike<number>;
  high: ArrayLike<number>;
  low: ArrayLike<number>;
  close: ArrayLike<number>;
};

/** One aggregated bucket (trading day or session-aligned intraday bucket), absolute ticks. */
export type Agg = { key: number; firstTs: number; high: number; low: number; close: number };

export type LevelId =
  "PD-H" | "PD-L" | "PD-C" | "ON-H" | "ON-L" | "S-H" | "S-L" | "4H-H" | "4H-L" | "15M-H" | "15M-L";

export type KeyLevel = { id: LevelId; ticks: number };

/** Every level id in display order (settings, legends). */
export const LEVEL_IDS: readonly LevelId[] = [
  "PD-H",
  "PD-L",
  "PD-C",
  "ON-H",
  "ON-L",
  "S-H",
  "S-L",
  "4H-H",
  "4H-L",
  "15M-H",
  "15M-L",
];

/** True when `periodMs` time bars nest exactly inside `bucketMs` buckets (both session-aligned). */
export function nests(periodMs: number, bucketMs: number): boolean {
  return periodMs > 0 && periodMs <= bucketMs && bucketMs % periodMs === 0;
}

/**
 * Aggregate bars into buckets keyed by `keyOf(openTs)`, oldest first. Keys must be non-decreasing
 * in time (trading days, bucket opens).
 */
export function aggregate(b: LevelBars, keyOf: (ts: number) => number, from = 0): Agg[] {
  const out: Agg[] = [];
  const o = b.origin ?? 0;
  let cur: Agg | null = null;
  for (let i = Math.max(0, from); i < b.length; i++) {
    const ts = b.openTs[i] ?? 0;
    const k = keyOf(ts);
    const hi = (b.high[i] ?? 0) + o;
    const lo = (b.low[i] ?? 0) + o;
    const cl = (b.close[i] ?? 0) + o;
    if (cur === null || cur.key !== k) {
      cur = { key: k, firstTs: ts, high: hi, low: lo, close: cl };
      out.push(cur);
    } else {
      if (hi > cur.high) cur.high = hi;
      if (lo < cur.low) cur.low = lo;
      cur.close = cl;
    }
  }
  return out;
}

/** Trading-day aggregates (key = trading day id, `calendar.ts`). */
export function dailyAggregates(b: LevelBars, from = 0): Agg[] {
  const days = new TradingDayCache();
  return aggregate(b, (ts) => days.of(ts), from);
}

/** Session-aligned intraday bucket aggregates (key = bucket open, UTC ms). */
export function bucketAggregates(b: LevelBars, bucketMs: number, from = 0): Agg[] {
  const days = new TradingDayCache();
  return aggregate(b, (ts) => days.bucketOpen(ts, bucketMs), from);
}

/** First bar index at or after `ts` (bars are in time order); `length` when none. */
export function firstIndexAtOrAfter(b: LevelBars, ts: number): number {
  let lo = 0;
  let hi = b.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((b.openTs[mid] ?? 0) < ts) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Key levels from the current trading day (the last bar's) and the one before:
 *
 * - **PD-H / PD-L**: prior trading day's high / low (the latest day with bars before the current).
 * - **PD-C**: that day's close = the close of its last bar (as available from bars; not the
 *   exchange settlement price, which the chart does not receive). Session overlay B3, own switch.
 * - **S-H / S-L**: current trading day's high / low so far (17:00 CT open → now).
 * - **ON-H / ON-L**: overnight = the current trading day before the 08:30 CT RTH open.
 * - **4H-H / 4H-L**, **15M-H / 15M-L**: the last *completed* session-aligned 4 h / 15 min bucket
 *   (the one before the bucket holding the last bar).
 *
 * Returns [] unless `periodMs` is an intraday time period; a level is absent when its data is.
 */
export function keyLevels(b: LevelBars, periodMs: number): KeyLevel[] {
  if (b.length === 0 || b.origin === null || !(periodMs > 0) || periodMs >= DAY_MS) return [];
  const out: KeyLevel[] = [];
  const lastTs = b.openTs[b.length - 1] ?? 0;
  const curDay = tradingDayOf(lastTs);
  // Only the last few trading days matter: start two calendar weeks back (covers weekends/holidays).
  const from = firstIndexAtOrAfter(b, lastTs - 14 * DAY_MS);
  const days = dailyAggregates(b, from);
  const cur = days.at(-1);
  const prev = days.length >= 2 ? days[days.length - 2] : undefined;
  if (prev)
    out.push(
      { id: "PD-H", ticks: prev.high },
      { id: "PD-L", ticks: prev.low },
      { id: "PD-C", ticks: prev.close },
    );
  const on = overnightOf(b, curDay, from);
  if (on) out.push({ id: "ON-H", ticks: on.high }, { id: "ON-L", ticks: on.low });
  if (cur) out.push({ id: "S-H", ticks: cur.high }, { id: "S-L", ticks: cur.low });
  for (const [bucket, hi, lo] of [
    [4 * HOUR, "4H-H", "4H-L"],
    [15 * MIN, "15M-H", "15M-L"],
  ] as const) {
    if (!nests(periodMs, bucket)) continue;
    const agg = bucketAggregates(b, bucket, firstIndexAtOrAfter(b, lastTs - 2 * DAY_MS - bucket));
    const done = agg.length >= 2 ? agg[agg.length - 2] : undefined;
    if (done) out.push({ id: hi, ticks: done.high }, { id: lo, ticks: done.low });
  }
  return out;
}

/** High/low of the current trading day's bars before the RTH open, or null without any. */
export function overnightOf(
  b: LevelBars,
  curDay: number,
  from = 0,
): { high: number; low: number; close: number } | null {
  const o = b.origin ?? 0;
  const rthOpen = centralToUtcMs(curDay, RTH_OPEN_MINUTE);
  const days = new TradingDayCache();
  let hi = -Infinity;
  let lo = Infinity;
  let close = 0;
  for (let i = Math.max(0, from); i < b.length; i++) {
    const ts = b.openTs[i] ?? 0;
    if (ts >= rthOpen) break;
    if (days.of(ts) !== curDay) continue;
    hi = Math.max(hi, (b.high[i] ?? 0) + o);
    lo = Math.min(lo, (b.low[i] ?? 0) + o);
    close = (b.close[i] ?? 0) + o;
  }
  return hi === -Infinity ? null : { high: hi, low: lo, close };
}

/**
 * High/low of the current trading day's **RTH** bars (open at or after 08:30 CT and before 15:00
 * CT on the day's own date, DST-aware), or null before the RTH open (owner Q-G1, 2026-09-28: the
 * stats box DR is the RTH range only). The complement of `overnightOf` within the day: a bar that
 * opens before 08:30 (e.g. a session-aligned 1 h bar at 08:00) counts as overnight.
 */
export function rthRangeOf(b: LevelBars, curDay: number, from = 0): { high: number; low: number } | null {
  const o = b.origin ?? 0;
  const open = centralToUtcMs(curDay, RTH_OPEN_MINUTE);
  const close = centralToUtcMs(curDay, RTH_CLOSE_MINUTE);
  let hi = -Infinity;
  let lo = Infinity;
  for (let i = Math.max(from, firstIndexAtOrAfter(b, open)); i < b.length; i++) {
    const ts = b.openTs[i] ?? 0;
    if (ts >= close) break;
    hi = Math.max(hi, (b.high[i] ?? 0) + o);
    lo = Math.min(lo, (b.low[i] ?? 0) + o);
  }
  return hi === -Infinity ? null : { high: hi, low: lo };
}

// ------------------------------------------------------------------------------ market phase

export type MarketPhase = "rth" | "eth" | "closed";

/**
 * CME equity-index session phase at a UTC instant (status bar): **RTH** 08:30–15:00 CT Mon–Fri,
 * **closed** in the 16:00–17:00 CT maintenance break and from Friday 16:00 to Sunday 17:00 CT,
 * **ETH** otherwise. Holidays are not modelled (as in `calendar.ts`).
 */
export function marketPhase(utcMs: number): MarketPhase {
  const local = utcMs + centralOffsetMs(utcMs);
  const day = Math.floor(local / DAY_MS);
  const minute = Math.floor((local - day * DAY_MS) / MIN);
  const wd = weekday(day);
  if (wd === 6) return "closed";
  if (wd === 0 && minute < 17 * 60) return "closed";
  if (wd === 5 && minute >= 16 * 60) return "closed";
  if (minute >= 16 * 60 && minute < 17 * 60) return "closed";
  if (wd !== 0 && minute >= RTH_OPEN_MINUTE && minute < RTH_CLOSE_MINUTE) return "rth";
  return "eth";
}

// ------------------------------------------------------------------------------------ averages

/** Wilder average of `xs` (length n): SMA of the first n seeds, then a ← (a·(n−1) + x) / n. */
export function wilder(xs: readonly number[], n = WILDER_N): { value: number; approx: boolean } | null {
  if (xs.length === 0) return null;
  if (xs.length < n) {
    let s = 0;
    for (const x of xs) s += x;
    return { value: s / xs.length, approx: true };
  }
  let a = 0;
  for (let i = 0; i < n; i++) a += xs[i] ?? 0;
  a /= n;
  for (let i = n; i < xs.length; i++) a = (a * (n - 1) + (xs[i] ?? 0)) / n;
  return { value: a, approx: false };
}

/**
 * True ranges of daily aggregates in ticks (exact integers): max(H, prev C) − min(L, prev C); the
 * first day (no previous close) is H − L.
 */
export function trueRanges(days: readonly Agg[]): number[] {
  return days.map((d, i) => {
    const pc = i > 0 ? days[i - 1]?.close : undefined;
    return pc === undefined ? d.high - d.low : Math.max(d.high, pc) - Math.min(d.low, pc);
  });
}

/**
 * Wilder RSI(n) of a close series, 0–100. With fewer than n changes the seed averages what exists
 * and `approx` is set; flat series → 50.
 */
export function rsi(closes: readonly number[], n = WILDER_N): { value: number; approx: boolean } | null {
  if (closes.length < 2) return null;
  const gains: number[] = [];
  const losses: number[] = [];
  for (let i = 1; i < closes.length; i++) {
    const ch = (closes[i] ?? 0) - (closes[i - 1] ?? 0);
    gains.push(ch > 0 ? ch : 0);
    losses.push(ch < 0 ? -ch : 0);
  }
  const g = wilder(gains, n);
  const l = wilder(losses, n);
  if (!g || !l) return null;
  const approx = g.approx;
  if (l.value === 0) return { value: g.value === 0 ? 50 : 100, approx };
  return { value: 100 - 100 / (1 + g.value / l.value), approx };
}

// ----------------------------------------------------------------------------------- stats box

export type Direction = -1 | 0 | 1;

export type StatsBox = {
  /** Last price − prior trading day's close, ticks; null without a prior day. */
  today: number | null;
  /** `today` as a percentage of the prior close (absolute price). */
  todayPct: number | null;
  /** Close of the last completed 1 h bucket (absolute ticks): the price where 1 h ROC flips sign. */
  flip: number | null;
  /** Daily ATR(14), Wilder, in ticks (fractional); completed trading days only. */
  atr: number | null;
  /** Fewer than 14 completed days: ATR is the mean of what exists ("~" in the box). */
  atrApprox: boolean;
  /**
   * Current trading day's **RTH** range (high − low of the 08:30–15:00 CT bars), ticks; null before
   * the RTH open (owner Q-G1, 2026-09-28).
   */
  dr: number | null;
  /** DR ÷ ATR × 100. */
  atrPct: number | null;
  /** Overnight range (ON-H − ON-L) ÷ ATR × 100. */
  onPct: number | null;
  /** RSI(14) of 1 h / 4 h / daily closes (current bucket's last close included). */
  rsi: { h1: number | null; h4: number | null; d: number | null };
  /** An RSI seeded from fewer than 14 changes. */
  rsiApprox: { h1: boolean; h4: boolean; d: boolean };
  /** ROC direction per timeframe: sign(last − previous bucket's close). */
  roc: { h1: Direction | null; h4: Direction | null; d: Direction | null };
};

export const EMPTY_STATS: StatsBox = {
  today: null,
  todayPct: null,
  flip: null,
  atr: null,
  atrApprox: false,
  dr: null,
  atrPct: null,
  onPct: null,
  rsi: { h1: null, h4: null, d: null },
  rsiApprox: { h1: false, h4: false, d: false },
  roc: { h1: null, h4: null, d: null },
};

const sign = (x: number): Direction => (x > 0 ? 1 : x < 0 ? -1 : 0);

function rocOf(aggs: readonly Agg[], last: number): Direction | null {
  const prev = aggs.length >= 2 ? aggs[aggs.length - 2] : undefined;
  return prev ? sign(last - prev.close) : null;
}

/**
 * The stats box. `tickPrice` = price of one tick (tickNum / tickDen) for `todayPct` only.
 * Returns EMPTY_STATS unless `periodMs` is an intraday time period.
 */
export function statsBox(b: LevelBars, periodMs: number, tickPrice: number): StatsBox {
  if (b.length === 0 || b.origin === null || !(periodMs > 0) || periodMs >= DAY_MS) return EMPTY_STATS;
  const o = b.origin;
  const last = (b.close[b.length - 1] ?? 0) + o;
  const days = dailyAggregates(b);
  const cur = days.at(-1);
  const done = days.slice(0, -1);
  const prev = done.at(-1);
  const today = prev ? last - prev.close : null;
  const prevPrice = prev ? prev.close * tickPrice : 0;
  const todayPct = today !== null && prevPrice !== 0 ? ((today * tickPrice) / prevPrice) * 100 : null;
  const atrW = wilder(trueRanges(done));
  const atr = atrW && atrW.value > 0 ? atrW.value : null;
  const curFrom = cur ? firstIndexAtOrAfter(b, cur.firstTs) : 0;
  const rth = cur ? rthRangeOf(b, cur.key, curFrom) : null;
  const dr = rth ? rth.high - rth.low : null;
  const on = cur ? overnightOf(b, cur.key, curFrom) : null;
  const pct = (x: number | null): number | null => (x === null || atr === null ? null : (x / atr) * 100);

  const h1 = nests(periodMs, HOUR) ? bucketAggregates(b, HOUR) : null;
  const h4 = nests(periodMs, 4 * HOUR) ? bucketAggregates(b, 4 * HOUR) : null;
  const closes = (a: readonly Agg[] | null): number[] | null => (a ? a.map((x) => x.close) : null);
  const r1 = h1 ? rsi(closes(h1) ?? []) : null;
  const r4 = h4 ? rsi(closes(h4) ?? []) : null;
  const rd = rsi(days.map((d) => d.close));
  const flipAgg = h1 && h1.length >= 2 ? h1[h1.length - 2] : undefined;
  return {
    today,
    todayPct,
    flip: flipAgg ? flipAgg.close : null,
    atr,
    atrApprox: atrW?.approx ?? false,
    dr,
    atrPct: pct(dr),
    onPct: pct(on ? on.high - on.low : null),
    rsi: { h1: r1?.value ?? null, h4: r4?.value ?? null, d: rd?.value ?? null },
    rsiApprox: { h1: r1?.approx ?? false, h4: r4?.approx ?? false, d: rd?.approx ?? false },
    roc: { h1: h1 ? rocOf(h1, last) : null, h4: h4 ? rocOf(h4, last) : null, d: rocOf(days, last) },
  };
}
